/**
 * Font family definitions per ProductContext.
 *
 * Figma "Font" variable collection defines 3 roles:
 *   - display: heading/display font (Pilat or Pilat Wide)
 *   - compact: body/UI font (Aktiv Grotesk or Montserrat)
 *   - text: paragraph/reading font (same families as compact)
 *
 * The `heading` and `body` exports map to these Figma roles:
 *   heading → display
 *   body → compact (also used for text contexts)
 */

import type { ProductContext } from "./typography";

const FALLBACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const PILAT = `"Pilat", "Aktiv Grotesk", "Noto Sans", "Montserrat", ${FALLBACK}`;
const PILAT_WIDE = `"Pilat Wide", "Aktiv Grotesk", "Noto Sans", "Montserrat", ${FALLBACK}`;
const AKTIV_GROTESK = `"Aktiv Grotesk", "Noto Sans", "Montserrat", ${FALLBACK}`;

/** Heading/display font per context (Figma "Font" collection → `display` role). */
const displayFontByContext: Record<ProductContext, string> = {
  b2b: PILAT,
  b2c: PILAT_WIDE,
  corpsite: PILAT,
};

/** Paragraph/reading font per context (Figma "Font" collection → `text` role). */
const textFontByContext: Record<ProductContext, string> = {
  b2b: AKTIV_GROTESK,
  b2c: AKTIV_GROTESK,
  corpsite: PILAT,
};

/** Body/UI font (Figma `compact` role) — Aktiv Grotesk across all contexts. */
const bodyFont = AKTIV_GROTESK;

/** Static fonts object (defaults to B2B for backwards compatibility) */
export const fonts = {
  heading: displayFontByContext.b2b,
  body: bodyFont,
  /** Paragraph/reading font — Figma `text` role */
  text: textFontByContext.b2b,
  /** @deprecated Use `heading` or `body` instead */
  primary: bodyFont,
};

/** Returns context-aware font families */
export const getFonts = (productContext: ProductContext = "b2b") => ({
  heading: displayFontByContext[productContext],
  body: bodyFont,
  /** Paragraph/reading font — Figma `text` role (differs from `body` for corpsite) */
  text: textFontByContext[productContext],
  /** @deprecated Use `heading` or `body` instead */
  primary: bodyFont,
});

export const typography = {
  button: {
    fontFamily: fonts.body,
    fontWeight: "bold",
  },
};
